/*----------------------------------
- TLD INSIGHT PAGE
----------------------------------*/

/**
 * @docs docs/features/tld-intelligence
 * @adr  ADR-0054
 * @adr  ADR-0062
 * @rule Aggregates, the coarse quality band and one bounded sample only: no score, no
 *       popularity, no single sale, no word about where the sales data comes from, and every
 *       TLD link goes to a public-set page or to the agenda (D31). The page body is the shared
 *       `TldPage`; this file adds the website's shell, links, tracking and SEO/GEO tail.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { useEffect, useMemo } from 'preact/hooks';
import {
    TLD_HUB_PATH,
    formatTldLabel,
    tldInsightMarkdownPath,
    tldInsightPath,
    type PublicTldInsight,
    type PublicTldRegistrarPrice,
} from '@uniquedomains/components/contracts/tlds/public';
import { buildPublicSearchConciseMarkdownPath } from '@uniquedomains/core/domains/public-search';
import type { AskYourAIProps } from '@uniquedomains/components/ask-ai';
import TldPage from '@uniquedomains/components/tlds/page/TldPage';
import type { TldPageHeroAction } from '@uniquedomains/components/tlds/page/pageTypes';

import useContext from '@/client/context';
import { AGENDA_NEWSLETTER_BAND_CONTENT } from '@/client/catalogs/landing/agenda/pageContent';
import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { LANDING_WEBSITE_LINKS } from '@/client/catalogs/landing/publicLinks';
import { TLD_PAGE_CONTENT } from '@/client/catalogs/landing/tld/pageContent';
import HomeAskAiSection from '@/client/pages/landing/components/ask-ai/HomeAskAiSection';
import PageGeoBlock from '@/client/pages/landing/components/geo/PageGeoBlock';
import { buildDomainsGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { Footer } from '@/client/pages/landing/components/layout/Footer';
import { Header } from '@/client/pages/landing/components/layout/Header';
import RelatedLinks from '@/client/pages/landing/components/navigation/RelatedLinks';
import NewsletterInkBand from '@/client/pages/landing/components/newsletter/NewsletterInkBand';
import { PAGE_CONTAINER_CLASS } from '@/client/pages/landing/components/shared/pageStyles';
import { track } from '@/client/pages/landing/lib/analytics';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { EMPTY_LANDING_NAVIGATION } from '@/client/pages/landing/types';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';

import { buildTldAgendaHref } from '../../lib/presentation';
import { TLD_PAGE_ATTRIBUTION, buildTldRadarHref } from '../../lib/tldRadarHref';
import { toTldPageData } from '../../lib/tldPageData';
import { usePublishedTldPaths } from '../../lib/usePublishedTldPaths';
import { useTldGithubIntent } from '../../lib/useTldGithubIntent';
import TldPersonasCard from '../TldPersonasCard';
import TldSampleSection from './TldSampleSection';
import TldSiblingLinks from './TldSiblingLinks';

import '../../../shared.css';

/*----------------------------------
- TYPES
----------------------------------*/

type TldInsightPageProps = {
    insight: PublicTldInsight;
    navigation?: LandingHeaderDomainsNavigation | null;
    /** The server's UTC day `YYYY-MM-DD` the page was rendered on, never the browser clock. */
    today: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const TRACKING_PAGE = 'tld_page';
const SUBSCRIBE_ANCHOR_ID = 'subscribe';

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function TldInsightPage({ insight, navigation, today }: TldInsightPageProps) {
    const content = TLD_PAGE_CONTENT.insight;
    const { slug } = insight;
    const tld = formatTldLabel(slug);
    const pagePath = tldInsightPath(slug);
    const radarHref = buildTldRadarHref(slug);
    const { publicOrigins } = useContext();
    const publishedPaths = usePublishedTldPaths();
    // `cta: 'hub'` is the way back to the hub, from each of the page's three links to it.
    const trackHubLink = () => track('tld_page_cta_click', { cta: 'hub', tld: slug, href: TLD_HUB_PATH });
    // One trigger for both Radar entries; the section tells the hero pill from the phone bar.
    const trackRadar = (section: 'tld_radar' | 'tld_mobile_bar') => {
        trackConversionTrigger('website_tld_page_create_radar', { href: radarHref, page: TRACKING_PAGE, section });
        track('tld_page_cta_click', { cta: 'radar', tld: slug, href: radarHref });
    };
    const trackGithub = (href: string) => {
        trackConversionTrigger('website_tld_page_github_link', { href, page: TRACKING_PAGE, section: 'tld_github' });
        track('github_repo_open_attempt', { section: 'tld_github', target: 'tld_repository' });
        track('tld_page_cta_click', { cta: 'github', tld: slug, href });
    };
    const trackRegistrar = (registrar: PublicTldRegistrarPrice, href: string) => {
        trackConversionTrigger('website_tld_page_registrar', { href, page: TRACKING_PAGE, section: 'tld_costs' });
        track('tld_page_cta_click', { cta: 'registrar', tld: slug, registrar: registrar.registrar, href });
    };
    useTldGithubIntent(slug, insight.githubRepoUrl);

    useEffect(() => {
        track('page_view', { page: TRACKING_PAGE, path: window.location.pathname, tld: slug });
    }, [slug]);

    const geoContext = useMemo(
        () =>
            buildDomainsGeoContext({
                currentPath: pagePath,
                fullMarkdownMirrorPath: tldInsightMarkdownPath(slug),
                hasSearchFilters: false,
                markdownMirrorPath: buildPublicSearchConciseMarkdownPath('tld', slug),
                previewRows: [],
                tld: slug,
            }),
        [pagePath, slug],
    );
    const askAiContext = useMemo<AskYourAIProps>(
        () => ({
            pageType: 'tld',
            pageUrl: new URL(pagePath, publicOrigins.websiteOrigin).toString(),
            pageTitle: content.meta.pageTitle(tld),
            tld: slug,
            geoContext,
            previewRows: insight.sample.map((row) => ({
                name: row.domain,
                tld: slug,
                status: row.status,
                ...(row.price !== null ? { pricePurchase: row.price } : {}),
            })),
        }),
        [content.meta, geoContext, insight.sample, pagePath, publicOrigins.websiteOrigin, slug, tld],
    );
    const { githubRepoUrl } = insight;
    const heroActions: TldPageHeroAction[] = [
        {
            id: 'radar',
            label: content.hero.followWithRadar(tld),
            href: radarHref,
            tone: 'white',
            arrow: true,
            onClick: () => trackRadar('tld_radar'),
            testId: 'tld-page-radar-cta',
            attribution: TLD_PAGE_ATTRIBUTION,
        },
        ...(githubRepoUrl
            ? [{
                id: 'dataset',
                label: content.hero.getDataset,
                href: githubRepoUrl,
                tone: 'outline' as const,
                external: true,
                onClick: () => trackGithub(githubRepoUrl),
                testId: 'tld-page-github-link',
            }]
            : []),
    ];
    const relatedLinks = [
        { label: content.related.hub, href: TLD_HUB_PATH, onClick: trackHubLink },
        { label: content.agendaLink(tld), href: buildTldAgendaHref(slug) },
        { label: content.related.radars, href: LANDING_WEBSITE_PAGE_ROUTES.productRadars },
    ];
    return (
        <div class="ud-landing-shell min-h-screen text-slate-950">
            <div class="relative z-10">
                <Header
                    links={LANDING_WEBSITE_LINKS}
                    navigation={navigation ?? EMPTY_LANDING_NAVIGATION}
                    currentPath={pagePath}
                    trackingPage={TRACKING_PAGE}
                    mobileActions={{
                        secondary: { label: content.mobileActions.agenda, href: buildTldAgendaHref(slug), testId: 'tld-page-mobile-agenda' },
                        // The design's `#signup` is the signup wall the hero's Radar pill opens.
                        primary: {
                            label: content.mobileActions.radar,
                            href: radarHref,
                            onClick: () => trackRadar('tld_mobile_bar'),
                            testId: 'tld-page-mobile-radar',
                        },
                    }}
                />

                <main id="top" data-testid="tld-page" data-tld={slug}>
                    <div class={`${PAGE_CONTAINER_CLASS} pt-11`}>
                        <TldPage
                            testIdPrefix="tld-page"
                            today={today}
                            copy={content.page}
                            data={toTldPageData(insight)}
                            hrefs={{ back: TLD_HUB_PATH }}
                            heroActions={heroActions}
                            backLinkVisibility="always"
                            onBackClick={trackHubLink}
                            onRegistrarClick={trackRegistrar}
                            slots={{
                                // Under "Market size" (ADR-0062 amendment of 2026-10-05): the ten-name
                                // sample, which names no sale.
                                afterMarket: <TldSampleSection slug={slug} sample={insight.sample} />,
                                bottomRight: (
                                    <TldSiblingLinks
                                        slug={slug}
                                        relatedSlugs={insight.relatedSlugs}
                                        publishedPaths={publishedPaths}
                                        apiUrl={insight.apiUrl}
                                        onHubClick={trackHubLink}
                                    />
                                ),
                            }}
                        />
                    </div>

                    <div class={`${PAGE_CONTAINER_CLASS} pt-6 lg:pt-8`}>
                        <TldPersonasCard
                            testId="tld-page-conversion"
                            attribution={TLD_PAGE_ATTRIBUTION}
                            investorTriggerId="website_tld_page_investor"
                            founderTriggerId="website_tld_page_founder"
                            trackingPage={TRACKING_PAGE}
                            section="tld_page_personas"
                            surface="tld_page"
                        />
                    </div>

                    <div class={`${PAGE_CONTAINER_CLASS} pt-6 lg:pt-8`}>
                        <NewsletterInkBand
                            anchorId={SUBSCRIBE_ANCHOR_ID}
                            source="website_tld_page"
                            heading={AGENDA_NEWSLETTER_BAND_CONTENT.heading}
                            body={AGENDA_NEWSLETTER_BAND_CONTENT.body}
                            onSubscribed={() => track('tld_page_cta_click', { cta: 'newsletter', tld: slug })}
                        />
                    </div>

                    <RelatedLinks links={relatedLinks} title={content.related.title} />

                    <HomeAskAiSection context={askAiContext} />

                    <PageGeoBlock className="pb-4" geo={geoContext} surface="tld_insight" />
                </main>

                <Footer links={LANDING_WEBSITE_LINKS} sectionHrefPrefix="/" trackingPage={TRACKING_PAGE} />
            </div>
        </div>
    );
}
